/**
 * Hook for offline license cache management.
 *
 * Provides access to the offline cache status and methods to validate
 * and update the cache for Pro mode offline support.
 */

import { useState, useEffect, useCallback } from 'react';
import type {
  LicenseCacheValidationResult,
  LicenseCacheStatusInfo,
  LicenseCacheStatus,
} from '../../../types/electron';
import { onAuthStateChange } from '../services/authService';

interface UseLicenseCacheReturn {
  /** Whether the cache is being loaded */
  isLoading: boolean;
  /** Cache validation result */
  cacheValidation: LicenseCacheValidationResult | null;
  /** Cache status for UI display */
  cacheStatus: LicenseCacheStatusInfo | null;
  /** Whether Pro access should be granted based on cache */
  hasOfflineProAccess: boolean;
  /** Whether to show the cache expiring warning */
  showCacheWarning: boolean;
  /** Days remaining in cache validity */
  daysRemaining: number;
  /** Whether clock rollback was detected */
  clockRollbackDetected: boolean;
  /** Hours of clock rollback (if detected) */
  rollbackHours: number | null;
  /** Whether Pro was blocked due to clock rollback */
  blockedByClockRollback: boolean;
  /** Validate the cache */
  validateCache: () => Promise<LicenseCacheValidationResult>;
  /** Update the cache with fresh subscription data */
  updateCache: (
    subscriptionStatus: LicenseCacheStatus,
    subscriptionExpiresAt?: Date,
    userId?: string,
    userEmail?: string
  ) => Promise<void>;
  /** Clear the cache (e.g., on logout) */
  clearCache: () => Promise<void>;
}

/**
 * Hook for managing offline license cache.
 *
 * Usage:
 * ```tsx
 * const { hasOfflineProAccess, showCacheWarning, daysRemaining } = useLicenseCache();
 *
 * // Show warning banner when cache is expiring
 * if (showCacheWarning) {
 *   return <OfflineLicenseBanner daysRemaining={daysRemaining} />;
 * }
 * ```
 */
export function useLicenseCache(): UseLicenseCacheReturn {
  const [isLoading, setIsLoading] = useState(true);
  const [cacheValidation, setCacheValidation] = useState<LicenseCacheValidationResult | null>(null);
  const [cacheStatus, setCacheStatus] = useState<LicenseCacheStatusInfo | null>(null);

  const api = window.electronAPI?.licenseCache;

  const validateCache = useCallback(async (): Promise<LicenseCacheValidationResult> => {
    if (!api) {
      return {
        isValid: false,
        hasProAccess: false,
        daysRemaining: 0,
        showWarning: false,
        error: 'License cache not available',
      };
    }

    // Use async validation which verifies signed token + clock rollback
    const result = await api.validateCacheAsync();
    setCacheValidation(result);
    return result;
  }, [api]);

  const updateCache = useCallback(
    async (
      subscriptionStatus: LicenseCacheStatus,
      subscriptionExpiresAt?: Date,
      userId?: string,
      userEmail?: string
    ): Promise<void> => {
      if (!api) return;

      await api.updateCache(
        subscriptionStatus,
        subscriptionExpiresAt?.toISOString(),
        userId,
        userEmail
      );

      // Revalidate cache after update
      await validateCache();
    },
    [api, validateCache]
  );

  const clearCache = useCallback(async (): Promise<void> => {
    if (!api) return;

    await api.clearCache();
    setCacheValidation(null);
    setCacheStatus(null);
  }, [api]);

  const fetchCacheStatus = useCallback(async (): Promise<void> => {
    if (!api) return;

    const status = await api.getCacheStatus();
    setCacheStatus(status);
  }, [api]);

  // Initialize cache state on mount
  useEffect(() => {
    const initialize = async () => {
      setIsLoading(true);
      await validateCache();
      await fetchCacheStatus();
      setIsLoading(false);
    };

    initialize();
  }, [validateCache, fetchCacheStatus]);

  // Re-validate when auth refreshes so the warning banner clears promptly after
  // a successful (re-)sign-in instead of waiting for the next remount. The
  // main-process session-health scheduler also fires TOKEN_REFRESHED, which
  // keeps a long-running session's banner state current.
  useEffect(() => {
    const unsubscribe = onAuthStateChange((event) => {
      if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
        void validateCache();
        void fetchCacheStatus();
      }
    });
    return unsubscribe;
  }, [validateCache, fetchCacheStatus]);

  // Extract clock rollback info from cache validation
  const clockValidation = cacheValidation?.clockValidation;
  const clockRollbackDetected = clockValidation?.rollbackDetected ?? false;
  const rollbackHours = clockValidation?.rollbackHours ?? null;
  const blockedByClockRollback = cacheValidation?.blockedByClockRollback ?? false;

  // Show warning if cache is expiring OR if clock rollback detected
  const showWarning = (cacheValidation?.showWarning ?? false) || blockedByClockRollback;

  return {
    isLoading,
    cacheValidation,
    cacheStatus,
    hasOfflineProAccess: cacheValidation?.hasProAccess ?? false,
    showCacheWarning: showWarning,
    daysRemaining: cacheValidation?.daysRemaining ?? 0,
    clockRollbackDetected,
    rollbackHours,
    blockedByClockRollback,
    validateCache,
    updateCache,
    clearCache,
  };
}
